/**
 * Change Detection Service
 * 
 * Handles intelligent change detection for form updates.
 * Supports incremental updates for arrays and field-level changes.
 * 
 * @module ChangeDetectionService
 */

/**
 * Detects changes between original and current data
 * Supports incremental array updates for optimal API calls
 * 
 * @param {Object} original - Original data state
 * @param {Object} current - Current data state
 * @param {Array<string>} editableFields - Fields that can be edited
 * @returns {Object} Object containing only changed fields
 */
export const detectChanges = (original, current, editableFields = ['name', 'phoneNumber', 'role', 'facilityIds', 'departmentIds']) => {
  const changes = {};
  
  editableFields.forEach(field => {
    const originalVal = original?.[field];
    const currentVal = current?.[field];
    
    // Skip undefined/null values
    if (currentVal === undefined || currentVal === null) {
      return;
    }
    
    // Special handling for role field
    if (field === 'role' && Array.isArray(currentVal) && (currentVal.length === 0 || currentVal[0] === null)) {
      return;
    }
    
    // Handle arrays with intelligent updates
    if (Array.isArray(currentVal)) {
      if (currentVal.length === 0 || (currentVal.length === 1 && currentVal[0] === null)) {
        return;
      }
      
      const originalArray = Array.isArray(originalVal) ? originalVal : [];
      const addedItems = currentVal.filter(item => !originalArray.includes(item));
      const removedItems = originalArray.filter(item => !currentVal.includes(item));
      
      const sortedOriginal = [...originalArray].sort();
      const sortedCurrent = [...currentVal].sort();
      const arraysEqual = sortedOriginal.length === sortedCurrent.length && 
                         sortedOriginal.every((val, idx) => val === sortedCurrent[idx]);
      
      if (!arraysEqual) {
        // For facilityIds and departmentIds, always send complete array
        // These fields require complete data, not incremental updates
        if (field === 'facilityIds' || field === 'departmentIds') {
          changes[field] = currentVal; // Complete array
        } else if (addedItems.length > 0 && removedItems.length === 0) {
          // Only additions - send just the new items (incremental update)
          changes[field] = addedItems;
        } else {
          // Removals or mixed changes - send complete array
          changes[field] = currentVal;
        }
      }
    } else {
      // Handle regular fields
      if (originalVal !== currentVal) {
        changes[field] = currentVal;
      }
    }
  });
  
  return changes;
};

/**
 * Checks if any changes exist between original and current data
 * 
 * @param {Object} original - Original data state
 * @param {Object} current - Current data state
 * @param {Array<string>} editableFields - Fields that can be edited
 * @returns {boolean} True if changes exist
 */
export const hasChanges = (original, current, editableFields) => {
  const changes = detectChanges(original, current, editableFields);
  return Object.keys(changes).length > 0;
};

/**
 * Default service export
 */
const ChangeDetectionService = {
  detectChanges,
  hasChanges
};

export default ChangeDetectionService;
